import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Sparkles, 
  MessageSquare, 
  Palette, 
  Ruler, 
  Heart,
  ArrowRight,
  Gift,
  Camera,
  CheckCircle,
  Shield,
  Clock,
  ChevronDown,
  ChevronRight,
  Star,
  Users,
  Hammer,
  Eye,
  ThumbsUp,
  Send,
  Lightbulb,
  Target,
  Gem,
  PenTool,
  Zap,
  Award,
  HelpCircle,
  Package,
  BadgeCheck
} from 'lucide-react';

// Why choose custom
const WHY_CUSTOM = [
  {
    icon: Gem,
    title: 'Unico al Mondo',
    description: 'Il tuo prodotto sarà realizzato esclusivamente per te. Nessun altro al mondo ne possiederà uno identico.',
    highlight: true
  },
  {
    icon: Ruler,
    title: 'Misure Perfette',
    description: 'Dimensioni su misura per il tuo spazio o le tue esigenze. Niente compromessi, solo perfezione.'
  },
  {
    icon: Palette,
    title: 'Colori e Materiali',
    description: 'Scegli i colori, i tessuti, i legni o i metalli che preferisci. L\'artigiano li adatterà alla tua visione.'
  },
  {
    icon: PenTool,
    title: 'Personalizzazione Totale',
    description: 'Incisioni, dediche, monogrammi, modifiche al design. Ogni dettaglio può essere personalizzato.'
  },
  {
    icon: MessageSquare,
    title: 'Dialogo Diretto',
    description: 'Parli direttamente con chi realizzerà il tuo prodotto. Nessun intermediario, solo collaborazione.'
  },
  {
    icon: Camera,
    title: 'Segui la Creazione',
    description: 'Ricevi foto e aggiornamenti durante la lavorazione. Vedi il tuo pezzo prendere forma giorno dopo giorno.'
  }
];

// Process steps
const PROCESS_STEPS = [
  {
    number: '01',
    title: 'Descrivi la Tua Idea',
    description: 'Racconta cosa hai in mente: utilizzo, dimensioni, materiali preferiti, stile desiderato. Non preoccuparti se non hai tutti i dettagli, gli artigiani ti aiuteranno a definirli.',
    icon: Lightbulb,
    color: 'bg-amber-500',
    tips: [
      'Allega immagini di ispirazione',
      'Indica il budget approssimativo',
      'Specifica la data entro cui ti serve'
    ]
  },
  {
    number: '02',
    title: 'Pubblica o Invia Direttamente',
    description: 'Puoi pubblicare la richiesta per tutti gli artigiani della categoria, oppure inviarla direttamente a un artigiano specifico che hai notato.',
    icon: Send,
    color: 'bg-blue-500',
    tips: [
      'Richiesta pubblica = più proposte',
      'Richiesta diretta = artigiano scelto da te',
      'Puoi sempre contattare altri in seguito'
    ]
  },
  {
    number: '03',
    title: 'Ricevi le Proposte',
    description: 'Gli artigiani interessati ti invieranno proposte dettagliate con prezzo, tempi, materiali previsti e spesso bozzetti o esempi di lavori simili.',
    icon: MessageSquare,
    color: 'bg-purple-500',
    tips: [
      'Confronta più proposte con calma',
      'Chatta per chiarire ogni dubbio',
      'Guarda il portfolio degli artigiani'
    ]
  },
  {
    number: '04',
    title: 'Scegli e Paga in Sicurezza',
    description: 'Quando trovi la proposta perfetta, accettala e procedi al pagamento. Il compenso viene trattenuto da Stripe fino alla consegna.',
    icon: Shield,
    color: 'bg-green-500',
    tips: [
      'Pagamento protetto con Stripe',
      'L\'artigiano riceve solo a conferma',
      'Massima sicurezza per entrambi'
    ]
  },
  {
    number: '05',
    title: 'Segui la Lavorazione',
    description: 'L\'artigiano ti tiene aggiornato con foto del processo. Puoi vedere il tuo pezzo unico prendere vita, dalla materia prima al prodotto finito.',
    icon: Eye,
    color: 'bg-orange-500',
    tips: [
      'Ricevi notifiche ad ogni update',
      'Commenta e dai feedback',
      'Le foto restano nella tua dashboard'
    ]
  },
  {
    number: '06',
    title: 'Ricevi il Tuo Capolavoro',
    description: 'L\'artigiano spedisce il prodotto finito. Quando lo ricevi, conferma e rilascia il pagamento. Il tuo pezzo unico è finalmente tuo!',
    icon: Package,
    color: 'bg-teal-500',
    tips: [
      'Spedizione tracciata',
      '7 giorni per verificare',
      'Lascia una recensione'
    ]
  }
];

// Use cases / occasions
const USE_CASES = [
  {
    title: 'Regali Speciali',
    description: 'Compleanni importanti, anniversari, matrimoni. Un regalo su misura dice "ho pensato solo a te".',
    icon: Gift,
    examples: ['Gioiello con incisione', 'Album fotografico artigianale', 'Scultura commemorativa']
  },
  {
    title: 'Arredamento Perfetto',
    description: 'Mobili che si incastrano perfettamente nel tuo spazio, con lo stile che hai sempre sognato.',
    icon: Ruler,
    examples: ['Libreria su misura', 'Tavolo per il tuo living', 'Testiera del letto']
  },
  {
    title: 'Accessori Unici',
    description: 'Borse, portafogli, cinture e accessori che riflettono la tua personalità.',
    icon: Gem,
    examples: ['Borsa con le tue iniziali', 'Portafoglio in pelle scelta', 'Cintura artigianale']
  },
  {
    title: 'Oggetti d\'Arte',
    description: 'Ceramiche, vetri, sculture create appositamente per te o per un\'occasione speciale.',
    icon: Palette,
    examples: ['Vaso personalizzato', 'Piatto commemorativo', 'Scultura su commissione']
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Ho fatto realizzare un anello di fidanzamento con un design tutto mio. L'artigiano ha capito esattamente cosa volevo e il risultato ha superato ogni aspettativa. La mia fidanzata ha pianto dalla gioia.",
    author: "Alessandro T.",
    location: "Torino",
    product: "Anello in oro con zaffiro",
    rating: 5
  },
  {
    text: "Cercavo una libreria che si adattasse perfettamente alla nicchia del mio soggiorno. L'artigiano è venuto a prendere le misure, mi ha proposto tre bozzetti e ha realizzato esattamente ciò che immaginavo.",
    author: "Serena M.",
    location: "Bologna",
    product: "Libreria in noce nazionale",
    rating: 5
  },
  {
    text: "Per il 50° anniversario dei miei genitori volevo qualcosa di unico. Ho commissionato un piatto in ceramica con i loro nomi e la data. La qualità e l'attenzione ai dettagli sono state incredibili.",
    author: "Marco R.",
    location: "Napoli",
    product: "Piatto commemorativo in ceramica",
    rating: 5
  }
];

// FAQ
const FAQ_ITEMS = [
  {
    question: 'Quanto costa un prodotto su misura?',
    answer: 'I prezzi variano enormemente in base al tipo di prodotto, ai materiali, alla complessità e al tempo richiesto. Puoi indicare il tuo budget nella richiesta e gli artigiani ti proporranno soluzioni compatibili. Riceverai preventivi dettagliati prima di impegnarti.'
  },
  {
    question: 'Quanto tempo ci vuole?',
    answer: 'Dipende dal tipo di prodotto e dalla complessità. Un gioiello semplice può richiedere 1-2 settimane, un mobile su misura anche 2-3 mesi. Ogni proposta include i tempi stimati, e puoi indicare se hai una scadenza specifica.'
  },
  {
    question: 'E se il risultato non mi piace?',
    answer: 'Durante la lavorazione ricevi aggiornamenti e puoi dare feedback. Se alla fine il prodotto non corrisponde a quanto concordato, puoi aprire una contestazione prima di confermare la ricezione. Il pagamento è protetto fino alla tua conferma.'
  },
  {
    question: 'Posso chiedere modifiche durante la lavorazione?',
    answer: 'Piccole modifiche sono spesso possibili nelle fasi iniziali. Cambiamenti sostanziali potrebbero comportare costi aggiuntivi o allungamento dei tempi. L\'importante è comunicare tempestivamente con l\'artigiano.'
  },
  {
    question: 'Come faccio a spiegare bene cosa voglio?',
    answer: 'Non preoccuparti se non hai le idee chiare al 100%. Descrivi l\'utilizzo, lo stile che ti piace, allega immagini di ispirazione. Gli artigiani sono abituati a guidare i clienti nella definizione del progetto.'
  },
  {
    question: 'Posso richiedere una copia di un prodotto esistente?',
    answer: 'Puoi usare immagini come ispirazione, ma gli artigiani creeranno un pezzo originale, non una copia esatta. Questo garantisce l\'unicità e rispetta la proprietà intellettuale di altri designer.'
  },
  {
    question: 'Ho diritto al recesso sui prodotti su misura?',
    answer: 'Per i prodotti realizzati su misura o personalizzati, il diritto di recesso non si applica (Art. 59 Codice del Consumo). Per questo è importante definire bene i dettagli prima di confermare. La garanzia legale sui difetti rimane comunque attiva.'
  },
  {
    question: 'Posso incontrare l\'artigiano di persona?',
    answer: 'Dipende dall\'artigiano e dalla sua disponibilità. Alcuni accolgono visite in bottega, altri lavorano solo da remoto. Puoi sempre chiedere tramite la chat della piattaforma.'
  }
];

// Comparison table data
const COMPARISON_DATA = [
  { feature: 'Prodotto unico', catalog: false, custom: true },
  { feature: 'Misure personalizzate', catalog: false, custom: true },
  { feature: 'Scelta materiali/colori', catalog: 'Limitata', custom: true },
  { feature: 'Incisioni e dediche', catalog: 'A volte', custom: true },
  { feature: 'Dialogo con l\'artigiano', catalog: 'Possibile', custom: 'Garantito' },
  { feature: 'Aggiornamenti durante la creazione', catalog: false, custom: true },
  { feature: 'Tempi di consegna', catalog: 'Brevi', custom: 'Variabili' },
  { feature: 'Diritto di recesso', catalog: true, custom: false },
];

export default function CustomProductPage() {
  const [expandedFaq, setExpandedFaq] = useState(null);

  return (
    <>
      <Helmet>
        <title>Prodotto su Misura | Creazioni Artigianali Personalizzate | Rarifex</title>
        <meta name="description" content="Fai realizzare un prodotto unico su misura da artigiani italiani verificati. Descrivi la tua idea, ricevi proposte, segui la creazione. Pagamento protetto, pezzo esclusivo garantito." />
        <meta name="keywords" content="prodotto su misura, artigianato personalizzato, creazione su commissione, regalo personalizzato, mobili su misura, gioielli personalizzati, made in italy su misura" />
        <link rel="canonical" href="https://www.rarifex.com/prodotto-su-misura" />
        <meta property="og:title" content="Prodotto su Misura | Creazioni Artigianali Personalizzate" />
        <meta property="og:description" content="Fai realizzare un prodotto unico su misura da artigiani italiani. Descrivi la tua idea e ricevi proposte personalizzate." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/prodotto-su-misura" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-amber-50/50 via-white to-slate-50">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#DAA520]/15 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-[#DAA520]/15 text-[#B8860B] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                Creazioni Uniche
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                Il tuo prodotto,{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#DAA520] to-[#B8860B]">
                  unico al mondo
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed max-w-3xl mx-auto">
                Descrivi la tua idea e lascia che gli artigiani italiani la trasformino in realtà. 
                <strong className="text-slate-800"> Nessun catalogo, nessun compromesso. Solo la tua visione.</strong>
              </p>
              
              <div className="flex flex-wrap justify-center gap-4 mb-10">
                <Link to="/regalo-personalizzato">
                  <Button size="lg" className="bg-[#DAA520] hover:bg-[#B8860B] text-white px-8">
                    <Sparkles className="w-5 h-5 mr-2" />
                    Crea la Tua Richiesta
                  </Button>
                </Link>
                <Link to="/crafters">
                  <Button size="lg" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100">
                    <Users className="w-5 h-5 mr-2" />
                    Scopri gli Artigiani
                  </Button>
                </Link>
              </div>

              {/* Stats */}
              <div className="flex flex-wrap justify-center gap-8 md:gap-12 mt-8">
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-[#DAA520]">100%</div>
                  <div className="text-sm text-slate-600 mt-1">Su Misura</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-slate-800">Zero</div>
                  <div className="text-sm text-slate-600 mt-1">Intermediari</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-[#008080]">250+</div>
                  <div className="text-sm text-slate-600 mt-1">Categorie</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HERO IMAGE */}
        <section className="container mx-auto px-4 -mt-4 mb-16">
          <div className="max-w-5xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1452860606245-08befc0ff44b?w=1200&fit=crop"
                alt="Artigiano al lavoro su un pezzo personalizzato"
                className="w-full h-[300px] md:h-[400px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <p className="text-white text-lg md:text-xl font-medium max-w-2xl">
                  "Un prodotto su misura non è solo un oggetto. È la materializzazione di un'idea, 
                  un dialogo tra te e chi crea con le proprie mani."
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* WHY CUSTOM SECTION */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Perché scegliere un prodotto su misura?
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Quando il catalogo non basta, quando cerchi qualcosa che esista solo per te.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {WHY_CUSTOM.map((item, index) => (
                  <Card 
                    key={index}
                    className={`p-6 border-slate-200 hover:shadow-lg transition-all ${
                      item.highlight ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200' : 'bg-white'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                      item.highlight ? 'bg-[#DAA520]/20' : 'bg-slate-100'
                    }`}>
                      <item.icon className={`w-6 h-6 ${item.highlight ? 'text-[#DAA520]' : 'text-slate-600'}`} />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-slate-600">{item.description}</p>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* PROCESS STEPS */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-16">
                <div className="inline-flex items-center gap-2 bg-[#008080]/10 text-[#008080] px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Target className="w-4 h-4" />
                  Come Funziona
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Dalla tua idea al prodotto finito
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Un processo semplice e trasparente, con la massima protezione per il tuo acquisto.
                </p>
              </div>

              <div className="space-y-6">
                {PROCESS_STEPS.map((step, index) => (
                  <Card key={index} className="p-6 md:p-8 bg-white border-slate-200 hover:border-[#DAA520]/30 hover:shadow-lg transition-all">
                    <div className="flex flex-col md:flex-row gap-6">
                      {/* Step number and icon */}
                      <div className="flex md:flex-col items-center gap-4 md:gap-2">
                        <div className={`w-14 h-14 ${step.color} rounded-2xl flex items-center justify-center flex-shrink-0`}>
                          <step.icon className="w-7 h-7 text-white" />
                        </div>
                        <span className="text-2xl font-bold text-slate-300">{step.number}</span>
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-slate-900 mb-2">{step.title}</h3>
                        <p className="text-slate-600 mb-4">{step.description}</p>
                        
                        {/* Tips */}
                        <div className="bg-amber-50/50 rounded-xl p-4 border border-amber-100">
                          <ul className="space-y-1">
                            {step.tips.map((tip, tipIndex) => (
                              <li key={tipIndex} className="flex items-start gap-2 text-sm text-slate-600">
                                <CheckCircle className="w-4 h-4 text-[#DAA520] flex-shrink-0 mt-0.5" />
                                {tip}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              <div className="mt-10 text-center">
                <Link to="/regalo-personalizzato">
                  <Button size="lg" className="bg-[#DAA520] hover:bg-[#B8860B] text-white px-8">
                    Inizia Ora
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* USE CASES */}
        <section className="py-16 md:py-24 bg-slate-900">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Per quali occasioni?
                </h2>
                <p className="text-lg text-slate-300 max-w-2xl mx-auto">
                  Un prodotto su misura è perfetto per i momenti che contano davvero.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {USE_CASES.map((useCase, index) => (
                  <div 
                    key={index}
                    className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50 hover:border-[#DAA520]/50 transition-all"
                  >
                    <div className="w-12 h-12 bg-[#DAA520]/20 rounded-xl flex items-center justify-center mb-4">
                      <useCase.icon className="w-6 h-6 text-[#DAA520]" />
                    </div>
                    <h3 className="font-bold text-white mb-2">{useCase.title}</h3>
                    <p className="text-sm text-slate-400 mb-4">{useCase.description}</p>
                    <div className="space-y-1">
                      {useCase.examples.map((example, exIndex) => (
                        <div key={exIndex} className="flex items-center gap-2 text-xs text-slate-500">
                          <ChevronRight className="w-3 h-3 text-[#DAA520]" />
                          {example}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* COMPARISON TABLE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-white to-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Catalogo vs Su Misura
                </h2>
                <p className="text-lg text-slate-600">
                  Quale opzione fa per te? Ecco un confronto rapido.
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200">
                <div className="grid grid-cols-3 bg-slate-50 border-b border-slate-200">
                  <div className="p-4 font-semibold text-slate-700">Caratteristica</div>
                  <div className="p-4 font-semibold text-slate-700 text-center border-l border-slate-200">
                    <span className="hidden md:inline">Acquisto dal </span>Catalogo
                  </div>
                  <div className="p-4 font-semibold text-[#DAA520] text-center border-l border-slate-200 bg-amber-50/50">
                    <span className="hidden md:inline">Prodotto </span>Su Misura
                  </div>
                </div>
                
                {COMPARISON_DATA.map((row, index) => (
                  <div 
                    key={index}
                    className={`grid grid-cols-3 ${index < COMPARISON_DATA.length - 1 ? 'border-b border-slate-100' : ''}`}
                  >
                    <div className="p-4 text-sm text-slate-700">{row.feature}</div>
                    <div className="p-4 text-center border-l border-slate-100">
                      {row.catalog === true ? (
                        <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                      ) : row.catalog === false ? (
                        <span className="text-slate-300">—</span>
                      ) : (
                        <span className="text-sm text-slate-500">{row.catalog}</span>
                      )}
                    </div>
                    <div className="p-4 text-center border-l border-slate-100 bg-amber-50/30">
                      {row.custom === true ? (
                        <CheckCircle className="w-5 h-5 text-[#DAA520] mx-auto" />
                      ) : row.custom === false ? (
                        <span className="text-slate-300">—</span>
                      ) : (
                        <span className="text-sm text-[#B8860B] font-medium">{row.custom}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-center text-sm text-slate-500 mt-6">
                Non sai quale scegliere? <Link to="/come-acquistare" className="text-[#008080] hover:underline">Leggi la guida completa</Link>
              </p>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Storie di clienti soddisfatti
                </h2>
                <p className="text-lg text-slate-600">
                  Chi ha scelto un prodotto su misura non torna più indietro.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {TESTIMONIALS.map((testimonial, index) => (
                  <Card key={index} className="p-6 bg-white border-slate-200">
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <p className="text-slate-700 mb-4 italic text-sm leading-relaxed">"{testimonial.text}"</p>
                    <div className="bg-amber-50 rounded-lg p-3 mb-4">
                      <p className="text-xs text-amber-800 font-medium">{testimonial.product}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#DAA520]/10 rounded-full flex items-center justify-center">
                        <span className="text-[#DAA520] font-semibold">{testimonial.author[0]}</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{testimonial.author}</p>
                        <p className="text-xs text-slate-500">{testimonial.location}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-slate-200 text-slate-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <HelpCircle className="w-4 h-4" />
                  Domande Frequenti
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Tutto quello che devi sapere
                </h2>
              </div>

              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, index) => (
                  <div 
                    key={index}
                    className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                    >
                      <span className="font-medium text-slate-900 pr-4">{faq.question}</span>
                      <ChevronDown 
                        className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${
                          expandedFaq === index ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {expandedFaq === index && (
                      <div className="px-6 pb-4">
                        <p className="text-slate-600">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA SECTION */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-[#DAA520] to-[#B8860B]">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Sparkles className="w-12 h-12 text-white/80 mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Pronto a creare qualcosa di unico?
              </h2>
              <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                Descrivi la tua idea e lascia che gli artigiani italiani ti sorprendano 
                con le loro proposte. Nessun impegno, solo ispirazione.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/regalo-personalizzato">
                  <Button size="lg" className="bg-white text-[#B8860B] hover:bg-slate-100">
                    <Sparkles className="w-5 h-5 mr-2" />
                    Crea la Tua Richiesta
                  </Button>
                </Link>
                <Link to="/requests">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                    <Eye className="w-5 h-5 mr-2" />
                    Vedi Richieste Attive
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}
